import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, FormControl, FormLabel, Input, Select, Button, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText, Slider, SliderTrack, SliderFilledTrack, SliderThumb } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const FinanceToolsPersonalAsset: React.FC = () => {
  const [assetValue, setAssetValue] = useState(35000);
  const [deposit, setDeposit] = useState(7000);
  const [loanTerm, setLoanTerm] = useState(5);
  const [interestRate, setInterestRate] = useState(7.5);
  const [balloon, setBalloon] = useState(0);

  const loanAmount = assetValue - deposit;
  const monthlyRate = interestRate / 100 / 12;
  const months = loanTerm * 12;
  const balloonAmount = (assetValue * balloon) / 100;
  const financedAmount = loanAmount - balloonAmount;
  const monthlyRepayment = (financedAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/tools/personal-asset" fallbackTitle="Personal Asset Finance Calculator" fallbackDescription="Calculate financing options for personal assets" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">Personal Asset Calculator</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Calculate personal loan repayments</Text>
          </VStack>
        </Container>
      </Box>
      <Container maxW="container.xl" py={20} flex="1">
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10}>
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg">
            <Heading size="md" mb={6}>Asset Details</Heading>
            <VStack spacing={6} align="stretch">
              <FormControl>
                <FormLabel>Asset Value</FormLabel>
                <Input type="number" value={assetValue} onChange={(e) => setAssetValue(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>${assetValue.toLocaleString()}</Text>
              </FormControl>
              <FormControl>
                <FormLabel>Deposit Amount</FormLabel>
                <Input type="number" value={deposit} onChange={(e) => setDeposit(Number(e.target.value))} size="lg" />
                <Text fontSize="sm" color="gray.600" mt={1}>{((deposit / assetValue) * 100).toFixed(1)}% deposit</Text>
              </FormControl>
              <FormControl>
                <FormLabel>Loan Term: {loanTerm} years</FormLabel>
                <Slider value={loanTerm} onChange={setLoanTerm} min={1} max={7} step={1}>
                  <SliderTrack><SliderFilledTrack bg="blue.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>
              <FormControl>
                <FormLabel>Interest Rate: {interestRate}%</FormLabel>
                <Slider value={interestRate} onChange={setInterestRate} min={5} max={12} step={0.1}>
                  <SliderTrack><SliderFilledTrack bg="orange.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
              </FormControl>
              <FormControl>
                <FormLabel>Balloon Payment: {balloon}%</FormLabel>
                <Slider value={balloon} onChange={setBalloon} min={0} max={30} step={5}>
                  <SliderTrack><SliderFilledTrack bg="purple.500" /></SliderTrack>
                  <SliderThumb boxSize={6} />
                </Slider>
                <Text fontSize="sm" color="gray.600" mt={1}>${balloonAmount.toLocaleString()} final payment</Text>
              </FormControl>
              <Button colorScheme="cyan" size="lg">Calculate Repayment</Button>
            </VStack>
          </Box>
          <Box>
            <Box bg="gradient-to-br" bgGradient="linear(to-br, cyan.500, blue.600)" p={8} borderRadius="xl" color="white" textAlign="center" mb={6}>
              <Text fontSize="sm" textTransform="uppercase" mb={2}>Monthly Repayment</Text>
              <Heading size="3xl" mb={2}>${Math.round(monthlyRepayment).toLocaleString()}</Heading>
              <Text fontSize="sm">Based on {interestRate}% over {loanTerm} years</Text>
            </Box>
            <SimpleGrid columns={2} spacing={4} mb={6}>
              <Stat bg="blue.50" p={4} borderRadius="lg">
                <StatLabel>Loan Amount</StatLabel>
                <StatNumber fontSize="xl">${loanAmount.toLocaleString()}</StatNumber>
                <StatHelpText>Amount financed</StatHelpText>
              </Stat>
              <Stat bg="purple.50" p={4} borderRadius="lg">
                <StatLabel>Balloon Payment</StatLabel>
                <StatNumber fontSize="xl">${balloonAmount.toLocaleString()}</StatNumber>
                <StatHelpText>Due at end</StatHelpText>
              </Stat>
              <Stat bg="green.50" p={4} borderRadius="lg">
                <StatLabel>Total Paid</StatLabel>
                <StatNumber fontSize="xl">${Math.round(monthlyRepayment * months + balloonAmount).toLocaleString()}</StatNumber>
                <StatHelpText>Over {loanTerm} years</StatHelpText>
              </Stat>
              <Stat bg="orange.50" p={4} borderRadius="lg">
                <StatLabel>Total Interest</StatLabel>
                <StatNumber fontSize="xl">${Math.round((monthlyRepayment * months + balloonAmount) - loanAmount).toLocaleString()}</StatNumber>
                <StatHelpText>Cost of loan</StatHelpText>
              </Stat>
            </SimpleGrid>
            <Box bg="cyan.50" p={6} borderRadius="lg" border="1px solid" borderColor="cyan.200">
              <Heading size="sm" mb={3}>Suitable For</Heading>
              <VStack align="start" spacing={2} fontSize="sm">
                <Text>✓ Cars, motorcycles, boats</Text>
                <Text>✓ Caravans and campers</Text>
                <Text>✓ Personal equipment</Text>
                <Text>✓ Recreation vehicles</Text>
                <Text>✓ Terms up to 7 years</Text>
              </VStack>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceToolsPersonalAsset;
